"use client";

/**
 * shadcn-style wrapper around `@radix-ui/react-tooltip`, re-skinned to
 * the pcnshark-review brand vocabulary.
 *
 * Radix portals the tooltip content out to the `<body>` end — same
 * pattern as Dialog and DropdownMenu. That's the only reliable way to
 * escape ancestor `overflow:hidden` / `overflow-x-auto` stacks (the
 * feed table at `/dashboard/pcns` and several admin Card tables have
 * those). The cost is that the portaled content sits outside the
 * `[data-app="dashboard"]` token-bridge scope, so we restyle the
 * Content primitive with brand tokens directly instead of relying on
 * `bg-popover` / `text-popover-foreground` cascading in.
 *
 * `<TooltipProvider>` is mounted once at the root layout so callers
 * don't have to remember to wrap each instance — see `app/layout.tsx`.
 */

import * as React from "react";
import * as TooltipPrimitive from "@radix-ui/react-tooltip";
import { cn } from "@/lib/utils";

const TooltipProvider = TooltipPrimitive.Provider;
const Tooltip = TooltipPrimitive.Root;
const TooltipTrigger = TooltipPrimitive.Trigger;
const TooltipPortal = TooltipPrimitive.Portal;

const TooltipContent = React.forwardRef<
  React.ElementRef<typeof TooltipPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof TooltipPrimitive.Content>
>(({ className, sideOffset = 6, collisionPadding = 8, ...props }, ref) => (
  <TooltipPrimitive.Portal>
    <TooltipPrimitive.Content
      ref={ref}
      sideOffset={sideOffset}
      // collisionPadding keeps the tooltip from butting against the
      // viewport edge — Radix re-positions it (flip / shift) and this
      // adds a small margin so the result still looks intentional.
      collisionPadding={collisionPadding}
      className={cn(
        // Brand vocabulary: square corners, hairline `--rule` border,
        // cream `--paper` background, ink text. No shadow — the
        // marketing system uses hairline rules to delimit surfaces.
        "z-50 max-w-xs p-3 text-[13px]",
        "bg-[color:var(--paper)] text-[color:var(--ink)]",
        "border border-[color:var(--rule)]",
        // Match dropdown-menu's animation classes so a future
        // tailwindcss-animate plugin install picks both up at once.
        "data-[state=open]:animate-in data-[state=closed]:animate-out",
        "data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
        "data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95",
        "data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2",
        "data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2",
        className,
      )}
      {...props}
    />
  </TooltipPrimitive.Portal>
));
TooltipContent.displayName = TooltipPrimitive.Content.displayName;

export {
  Tooltip,
  TooltipTrigger,
  TooltipContent,
  TooltipProvider,
  TooltipPortal,
};
